import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Star, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Search, 
  Filter, 
  Calendar, 
  Award, 
  Eye, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight, 
  RotateCcw,
  UserCheck,
  Building2,
  Clock,
  Loader2,
  PieChart as PieIcon
} from 'lucide-react';
import { message, Modal, Tag } from 'antd';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import apiClient from '../../utils/apiClient';
import VietnameseDatePicker from '../../components/documents/VietnameseDatePicker';
import CustomSelect from '../../components/base/CustomSelect';

const SCORE_COLORS = {
  5: '#10B981',
  4: '#06B6D4',
  3: '#F59E0B',
  2: '#F97316',
  1: '#EF4444',
};

const stars = [5, 4, 3, 2, 1];
const scoreColor = (score) => score <= 2 ? 'text-rose-600' : score === 3 ? 'text-amber-600' : 'text-emerald-600';
const scoreBg = (score) => score <= 2 ? 'bg-rose-50 text-rose-700 border-rose-200' : score === 3 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';

export default function LeaderMeetingRatingsPage() {
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({
    totalRatings: 0,
    averageScore: 0,
    satisfactionRate: 0,
    scoreDistribution: [],
    byLeader: []
  });
  const [ratings, setRatings] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0
  });

  // Filter states
  const [search, setSearch] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Detail Modal state
  const [selectedRating, setSelectedRating] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch Statistics
  const fetchStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const res = await apiClient.get('/api/leader-meeting-ratings/statistics', { params });
      const statData = res.data?.data || res.data || {};
      setStats({
        totalRatings: statData.totalRatings || 0,
        averageScore: statData.averageScore || 0,
        satisfactionRate: statData.satisfactionRate || 0,
        scoreDistribution: Array.isArray(statData.scoreDistribution) ? statData.scoreDistribution : [],
        byLeader: Array.isArray(statData.byLeader) ? statData.byLeader : []
      });
    } catch (err) {
      console.error('Failed to fetch leader rating stats', err);
    } finally {
      setStatsLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch Ratings List
  const fetchRatings = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10
      };
      if (search.trim()) params.search = search.trim();
      if (scoreFilter) params.score = Number(scoreFilter);
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const res = await apiClient.get('/api/leader-meeting-ratings', { params });
      const raw = res.data?.data || res.data;
      const dataList = Array.isArray(raw) 
        ? raw 
        : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.items) ? raw.items : []));
      const paginationData = raw?.pagination || res.data?.pagination;

      setRatings(dataList);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (err) {
      console.error('Failed to fetch leader rating list', err);
    } finally {
      setLoading(false);
    }
  }, [search, scoreFilter, fromDate, toDate]);

  useEffect(() => {
    fetchStatistics();
    fetchRatings(1);
  }, [fetchStatistics, fetchRatings]);

  // Fetch Detail
  const handleOpenDetail = async (item) => {
    setSelectedRating(item);
    const targetId = item.id;
    if (!targetId) return;

    try {
      setDetailLoading(true);
      const res = await apiClient.get(`/api/leader-meeting-ratings/${targetId}`);
      const d = res.data?.data || res.data;
      if (d) {
        const reg = d.registration || {};
        const app = reg.applicant || {};
        const ldr = reg.leader || d.leader || {};
        setSelectedRating({
          ...item,
          ...d,
          receptionCode: reg.registrationCode || d.registrationCode || item.receptionCode || item.registrationCode,
          citizenName: app.fullName || d.applicantName || item.citizenName || item.applicantName,
          leaderName: ldr.fullName || d.leader?.fullName || item.leaderName,
          appointmentDate: reg.appointmentDate || d.appointmentDate || item.appointmentDate,
          timeSlot: reg.timeSlot || d.timeSlot || item.timeSlot,
          workingContent: reg.topic || reg.reason || d.topic || item.workingContent || item.topic,
          selectedSuggestions: d.selectedSuggestions || d.ly_do || [],
          comment: d.comment || d.nhan_xet || item.comment || ''
        });
      }
    } catch (err) {
      console.warn("Could not fetch remote detail, using current row data", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const scorePieData = useMemo(() => {
    const dist = stats.scoreDistribution || [];
    return [5, 4, 3, 2, 1].map(star => {
      const item = dist.find(d => (d.score || d.star) === star);
      const count = item?.count || item?.total || 0;
      return {
        name: `${star} Sao`,
        star,
        value: count,
        color: SCORE_COLORS[star]
      };
    }).filter(item => stats.totalRatings === 0 || item.value > 0);
  }, [stats]);

  // Render Star Icons
  const renderStars = (score) => {
    const numScore = Number(score) || 0;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`w-4 h-4 ${
              s <= numScore
                ? 'fill-amber-400 text-amber-400'
                : 'fill-gray-100 text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
            <UserCheck className="w-4 h-4" />
            Lịch gặp lãnh đạo
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Đánh giá gặp lãnh đạo
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Theo dõi mức độ hài lòng và ý kiến nhận xét của công dân sau các buổi tiếp và làm việc với Lãnh đạo.
          </p>
        </div>
      </div>

      {/* KPI STATS CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Ratings */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Tổng lượt đánh giá</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {stats.totalRatings || 0}
            </span>
            <span className="text-xs text-gray-500">lượt</span>
          </div>
        </div>

        {/* Average Score */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Điểm đánh giá TB</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Star className="w-4 h-4 fill-amber-400" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-amber-600">
              {stats.averageScore ? Number(stats.averageScore).toFixed(1) : '5.0'}
            </span>
            <span className="text-xs text-gray-500">/ 5.0</span>
          </div>
        </div>

        {/* Satisfaction Rate */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Tỷ lệ hài lòng (4-5★)</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-emerald-600">
              {stats.satisfactionRate ? `${stats.satisfactionRate}%` : '100%'}
            </span>
            <span className="text-xs text-emerald-600 font-medium">Hài lòng</span>
          </div>
        </div>

        {/* Positive Ratings */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Đánh giá xuất sắc (5★)</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Award className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-purple-600">
              {Array.isArray(stats.scoreDistribution) ? (stats.scoreDistribution.find(s => s.score === 5)?.count || 0) : 0}
            </span>
            <span className="text-xs text-gray-500">phiếu 5 sao</span>
          </div>
        </div>
      </div>

      {/* BIỂU ĐỒ TRÒN PHÂN BỐ SỐ SAO */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Donut Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
              <PieIcon className="w-4 h-4 text-emerald-600" />
              Tỷ lệ phân bố số sao
            </h3>
          </div>
          <div className="h-56 flex items-center justify-center">
            {stats.totalRatings === 0 ? (
              <div className="text-center text-gray-400 text-xs">Chưa có dữ liệu</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scorePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {scorePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(val, name) => [`${val} phiếu (${Math.round((val / stats.totalRatings) * 100)}%)`, name]}
                    contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    iconType="circle"
                    formatter={(val) => <span className="text-xs font-medium text-gray-700">{val}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Progress Breakdown Bars */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs lg:col-span-2 flex flex-col justify-center">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            Chi tiết số lượng theo từng mức sao
          </h3>
          <div className="space-y-3">
            {[5, 4, 3, 2, 1].map(star => {
              const count = stats.scoreDistribution?.find(d => (d.score || d.star) === star)?.count || 0;
              const percent = stats.totalRatings > 0 ? Math.round((count / stats.totalRatings) * 100) : 0;
              return (
                <div key={star} className="flex items-center gap-3 text-xs">
                  <span className="w-12 font-bold text-gray-700 flex items-center gap-1">
                    {star} <Star size={12} className="text-amber-500 fill-amber-500" />
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${percent}%`, 
                        backgroundColor: SCORE_COLORS[star] 
                      }}
                    />
                  </div>
                  <span className="w-20 text-right font-semibold text-gray-700">
                    {count} <span className="font-normal text-gray-400">({percent}%)</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FILTER & SEARCH */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="w-full md:w-80 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm mã đơn, tên công dân, lãnh đạo, nhận xét..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
            {/* Filter by Star */}
            <div className="w-48">
              <CustomSelect
                value={scoreFilter}
                onChange={(val) => setScoreFilter(typeof val === 'object' ? val?.target?.value : val)}
                placeholder="Tất cả số sao"
                options={[
                  { value: "", label: "Tất cả số sao" },
                  { value: "5", label: "5 sao - Rất hài lòng" },
                  { value: "4", label: "4 sao - Hài lòng" },
                  { value: "3", label: "3 sao - Bình thường" },
                  { value: "2", label: "2 sao - Chưa hài lòng" },
                  { value: "1", label: "1 sao - Rất không hài lòng" }
                ]}
              />
            </div>

            {/* Filter Date From */}
            <div className="w-36">
              <VietnameseDatePicker
                value={fromDate}
                maxDate={toDate || undefined}
                onChange={(isoVal) => setFromDate(isoVal)}
                placeholder="dd/MM/yyyy"
              />
            </div>

            {/* Filter Date To */}
            <div className="w-36">
              <VietnameseDatePicker
                value={toDate}
                minDate={fromDate || undefined}
                onChange={(isoVal) => setToDate(isoVal)}
                placeholder="dd/MM/yyyy"
              />
            </div>

            {/* Reset Filter Button */}
            {(search || scoreFilter || fromDate || toDate) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setScoreFilter('');
                  setFromDate('');
                  setToDate('');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-xs flex items-center gap-1"
                title="Xóa bộ lọc"
              >
                <RotateCcw size={14} />
                <span className="hidden sm:inline">Đặt lại</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-bold text-gray-700">
            Danh sách phiếu đánh giá ({pagination.totalItems || ratings.length})
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <th className="pl-6 pr-4 py-3.5 whitespace-nowrap" style={{ width: '13%' }}>
                  Mã đơn
                </th>
                <th className="px-4 py-3.5" style={{ width: '18%' }}>
                  Người dân
                </th>
                <th className="px-4 py-3.5" style={{ width: '18%' }}>
                  Lãnh đạo tiếp
                </th>
                <th className="px-4 py-3.5" style={{ width: '25%' }}>
                  Mức đánh giá & Nhận xét
                </th>
                <th className="px-4 py-3.5 whitespace-nowrap" style={{ width: '16%' }}>
                  Thời gian đánh giá
                </th>
                <th className="px-4 py-3.5 text-center whitespace-nowrap" style={{ width: '10%' }}>
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                    Đang tải danh sách đánh giá gặp lãnh đạo...
                  </td>
                </tr>
              ) : ratings.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-400">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    Không có phiếu đánh giá nào phù hợp.
                  </td>
                </tr>
              ) : (
                ratings.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                    {/* Mã đơn */}
                    <td className="pl-6 pr-4 py-3.5 font-bold text-blue-600 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleOpenDetail(item)}
                        className="hover:underline hover:text-blue-800 text-left font-bold text-blue-600 cursor-pointer transition-colors"
                        title="Xem chi tiết đánh giá"
                      >
                        {item.registrationCode || item.receptionCode || item.ticketNo || item.id}
                      </button>
                    </td>

                    {/* Người dân */}
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-gray-900 text-[13px] truncate">
                        {item.applicantName || item.citizenName || 'Công dân'}
                      </div>
                    </td>

                    {/* Lãnh đạo tiếp */}
                    <td className="px-4 py-3.5">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50/70 border border-blue-100 text-blue-800 text-xs font-semibold">
                        <UserCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span className="truncate max-w-[140px]" title={item.leader?.fullName || item.leaderName || item.officerName || 'Lãnh đạo UBND'}>
                          {item.leader?.fullName || item.leaderName || item.officerName || 'Lãnh đạo UBND'}
                        </span>
                      </div>
                    </td>

                    {/* Mức đánh giá & Nhận xét */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        {renderStars(item.score)}
                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${scoreBg(item.score)}`}>
                          {item.score} / 5
                        </span>
                      </div>
                      {item.comment && (
                        <p className="text-gray-600 text-xs mt-1 truncate max-w-[200px] italic" title={item.comment}>
                          "{item.comment}"
                        </p>
                      )}
                    </td>

                    {/* Thời gian đánh giá */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-gray-600">
                      <div className="font-medium text-gray-900 text-xs">
                        {item.ratedAt || item.createdAt ? new Date(item.ratedAt || item.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '---'}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {item.ratedAt || item.createdAt ? new Date(item.ratedAt || item.createdAt).toLocaleDateString('vi-VN') : (item.appointmentDate ? new Date(item.appointmentDate).toLocaleDateString('vi-VN') : '---')}
                      </div>
                    </td>

                    {/* Thao tác */}
                    <td className="px-4 py-3.5 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleOpenDetail(item)}
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-blue-600 transition-colors inline-flex items-center justify-center cursor-pointer shadow-2xs"
                        title="Xem chi tiết phiếu đánh giá"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination.totalPages > 1 && (
          <div className="p-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 text-xs text-gray-500">
            <span>
              Trang {pagination.currentPage} / {pagination.totalPages} ({pagination.totalItems} kết quả)
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={pagination.currentPage <= 1}
                onClick={() => fetchRatings(Math.max(1, pagination.currentPage - 1))}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={pagination.currentPage >= pagination.totalPages}
                onClick={() => fetchRatings(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL CHI TIẾT ĐÁNH GIÁ */}
      <Modal
        title={<div className="font-bold text-gray-900 text-base">Chi tiết phiếu đánh giá gặp lãnh đạo</div>}
        open={Boolean(selectedRating)}
        onCancel={() => setSelectedRating(null)}
        footer={[
          <button
            key="close"
            onClick={() => setSelectedRating(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Đóng
          </button>
        ]}
        width={650}
      >
        {detailLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
            <p className="text-xs text-gray-500 mt-2">Đang tải chi tiết...</p>
          </div>
        ) : selectedRating && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Mã đơn</span>
                <span className="font-bold text-blue-600 text-sm">{selectedRating.receptionCode || selectedRating.ticketNo || selectedRating.id}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Số sao đánh giá</span>
                <div className="flex items-center gap-2 mt-0.5">
                  {renderStars(selectedRating.score)}
                  <span className={`px-2 py-0.5 rounded text-xs font-bold border ${scoreBg(selectedRating.score)}`}>
                    {selectedRating.score} / 5
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Họ tên công dân</span>
                <span className="font-bold text-gray-900 text-sm">{selectedRating.citizenName || selectedRating.applicantName || 'Công dân'}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Lãnh đạo tiếp</span>
                <span className="font-bold text-blue-700 text-sm">
                  {selectedRating.leader?.fullName || selectedRating.leaderName || selectedRating.officerName || 'Lãnh đạo UBND'}
                </span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg sm:col-span-2">
                <span className="text-xs text-gray-500 block">Thời gian tiếp & đánh giá</span>
                <span className="font-medium text-gray-800 text-sm">
                  {selectedRating.timeSlot || ''} {selectedRating.receptionDate || ''} (Đánh giá lúc: {selectedRating.ratedAt || selectedRating.createdAt ? new Date(selectedRating.ratedAt || selectedRating.createdAt).toLocaleString('vi-VN') : '---'})
                </span>
              </div>
            </div>

            {selectedRating.workingContent && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1 font-semibold">Nội dung / Lý do buổi gặp</span>
                <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">{selectedRating.workingContent}</p>
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs text-gray-500 block mb-1 font-semibold">Nhận xét & Ý kiến của công dân</span>
              <p className="text-sm text-gray-800 italic whitespace-pre-wrap">
                {selectedRating.comment ? `"${selectedRating.comment}"` : 'Không có nhận xét thêm.'}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
